import { FC, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Flex, VStack } from '@chakra-ui/react';
import { addHours } from 'date-fns';

import { CompanyProfile } from '__generated__/GQL';
import useDebouncedCallback from 'common/hooks/useDebouncedCallback';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineLoader from 'components/_core/SunshineLoader';
import BackButton from 'components/BackButton';
import { parseTaxForecastPeriodInterval } from 'features/Accounting/pages/Estimation/common/utils';
import {
  UpdateTaxDataDocument,
  UpdateTaxDataMutationVariables,
} from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';
import { useTaxForecastData } from 'features/Accounting/pages/Estimation/hooks/useTaxForecastData';
import { formatDate, FORMATS } from 'helpers/date';
import * as Layout from 'layouts/ContentLayout';

import Heading from '../../../../../../../../components/_core/Heading';
import Text from '../../../../../../../../components/_core/Text';
import locales from '../../../../locales';
import EstimationCtaCard from '../../../EstimationCtaCard';
import { UpdateCompanyProfileTaxDetailsDocument } from './hooks/updateCompanyProfileTaxDetails/updateCompanyProfileTaxDetails.gql';

enum TaxOption {
  Accre = 'ACCRE',
  PrelevLib = 'PRELEV_LIB',
  IsSubjectToVat = 'IS_SUBJECT_TO_VAT',
}

enum TaxFrequency {
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
}

const TAX_FREQUENCY: Record<string, TaxFrequency> = {
  MONTHLY: TaxFrequency.Monthly,
  QUARTERLY: TaxFrequency.Quarterly,
};

interface EstimationSettingsProps {
  company: CompanyProfile;
}

const EstimationSettings: FC<EstimationSettingsProps> = ({ company }) => {
  const [selectedTaxOptions, setSelectedTaxOptions] = useState<TaxOption[]>(
    () => {
      const taxOptions: TaxOption[] = [];
      if (company?.hasAccre) {
        taxOptions.push(TaxOption.Accre);
      }
      if (company?.hasPrelevLib) {
        taxOptions.push(TaxOption.PrelevLib);
      }

      return taxOptions;
    },
  );

  const { currentTaxForecastPeriod, loading, taxForecastProfile } =
    useTaxForecastData({
      onCompleted: (data) => {
        if (data.viewer.company.taxForecastProfile?.isSubjectToVat) {
          setSelectedTaxOptions((options) => [
            ...options,
            TaxOption.IsSubjectToVat,
          ]);
        }
      },
    });

  const [selectedFrequency, setSelectedFrequency] = useState(
    TAX_FREQUENCY[company.taxFrequency],
  );
  const [updateCompanyProfileTaxDetails] = useMutation(
    UpdateCompanyProfileTaxDetailsDocument,
  );
  const [updateTaxData] = useMutation(UpdateTaxDataDocument);

  const currentPeriodInfo = useMemo(() => {
    if (!currentTaxForecastPeriod) {
      return null;
    }

    const startOfNewPeriod = addHours(currentTaxForecastPeriod.periodEndsAt, 1);
    const newPeriodActiveFrom = locales.formatString(
      locales.settings.frequency.activeFrom,
      { date: formatDate(startOfNewPeriod, FORMATS.P) },
    ) as string;

    return {
      interval: parseTaxForecastPeriodInterval(
        currentTaxForecastPeriod.periodStartsAt,
        currentTaxForecastPeriod.periodEndsAt,
      ),
      newPeriodActiveFrom,
    };
  }, [currentTaxForecastPeriod]);

  const onTaxFrequencyChange = useDebouncedCallback((value: TaxFrequency) => {
    updateCompanyProfileTaxDetails({
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          taxFrequency: value,
        },
      },
    });
  }, 500);

  const onTaxOptionsChange = useDebouncedCallback((options: TaxOption[]) => {
    const hasAccre = options.includes(TaxOption.Accre);
    const hasPrelevLib = options.includes(TaxOption.PrelevLib);
    const isSubjectToVat = options.includes(TaxOption.IsSubjectToVat);

    if (
      company.hasAccre !== hasAccre ||
      company.hasPrelevLib !== hasPrelevLib
    ) {
      updateCompanyProfileTaxDetails({
        variables: {
          input: {
            companyProfileId: company.companyProfileId,
            hasAccre,
            hasPrelevLib,
          },
        },
      });
    }

    if (taxForecastProfile?.isSubjectToVat !== isSubjectToVat) {
      let input: UpdateTaxDataMutationVariables['input'] = {
        companyProfileId: company.companyProfileId,
        taxForecastProfileData: { isSubjectToVat },
        taxFrequency: company.taxFrequency,
      };

      if (isSubjectToVat === false) {
        input = { ...input, taxForecastPeriodData: { vatAmount: null } };
      }

      updateTaxData({ variables: { input } });
    }
  }, 500);

  return (
    <Layout.Root>
      <Layout.Body>
        <BackButton />

        <Heading marginY="space-32" size="lg">
          {locales.settings.title}
        </Heading>

        <EstimationCtaCard
          ctaText={locales.settings.cta.text}
          subtitle={locales.settings.cta.subtitle}
          title={locales.settings.cta.title}
        />

        <VStack align="stretch" gap="space-20" marginTop="space-32">
          {loading ? (
            <SunshineLoader alignSelf="center" />
          ) : (
            <>
              <Flex flexDirection="column" gap="space-4">
                <Text size="sm">{locales.settings.frequency.description}</Text>

                <SunshineCard.RadioGroup
                  onChange={(value) => {
                    setSelectedFrequency(value as TaxFrequency);
                    onTaxFrequencyChange(value as TaxFrequency);
                  }}
                  value={selectedFrequency}
                >
                  <SunshineCard.Group>
                    <SunshineCard
                      title={locales.settings.frequency.monthly}
                      value={TaxFrequency.Monthly}
                      variant="radio"
                    >
                      {currentPeriodInfo?.interval ===
                      TaxFrequency.Quarterly ? (
                        <SunshineCard.Text>
                          {currentPeriodInfo.newPeriodActiveFrom}
                        </SunshineCard.Text>
                      ) : null}
                    </SunshineCard>

                    <SunshineCard
                      title={locales.settings.frequency.quarterly}
                      value={TaxFrequency.Quarterly}
                      variant="radio"
                    >
                      {currentPeriodInfo?.interval === TaxFrequency.Monthly ? (
                        <SunshineCard.Text>
                          {currentPeriodInfo.newPeriodActiveFrom}
                        </SunshineCard.Text>
                      ) : null}
                    </SunshineCard>
                  </SunshineCard.Group>
                </SunshineCard.RadioGroup>
              </Flex>

              <Flex flexDirection="column" gap="space-4">
                <Text size="sm">{locales.settings.options.description}</Text>

                <SunshineCard.CheckboxGroup<TaxOption>
                  onChange={(values) => {
                    setSelectedTaxOptions(values);
                    onTaxOptionsChange(values);
                  }}
                  value={selectedTaxOptions}
                >
                  <SunshineCard.Group>
                    <SunshineCard
                      title={locales.settings.options.accre}
                      value={TaxOption.Accre}
                      variant="checkbox"
                    />
                    <SunshineCard
                      title={locales.settings.options.liberatoire}
                      value={TaxOption.PrelevLib}
                      variant="checkbox"
                    >
                      <SunshineCard.Content>
                        <SunshineCard.Text>
                          {locales.settings.options.libDescription}
                        </SunshineCard.Text>
                      </SunshineCard.Content>
                    </SunshineCard>

                    <SunshineCard
                      title={locales.settings.options.vat}
                      value={TaxOption.IsSubjectToVat}
                      variant="checkbox"
                    />
                  </SunshineCard.Group>
                </SunshineCard.CheckboxGroup>
              </Flex>
            </>
          )}
        </VStack>
      </Layout.Body>
    </Layout.Root>
  );
};

export default EstimationSettings;
